<template>
  <div>
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-semibold text-gray-800">Physical &amp; Occupational Therapy</h2>
      <div class="flex items-center gap-3">
        <div class="inline-flex rounded border border-gray-300 overflow-hidden text-sm">
          <button class="px-3 py-1.5" :class="viewMode === 'facility' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'" @click="viewMode = 'facility'; selectedFacility = null; patientRows = []; cptCodes = []">By Facility</button>
          <button class="px-3 py-1.5 border-l border-gray-300" :class="viewMode === 'provider' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'" @click="viewMode = 'provider'; selectedFacility = null; patientRows = []; cptCodes = []">By Provider</button>
        </div>
        <select v-model="careSetting" class="px-2 py-1.5 text-sm border border-gray-300 rounded bg-white" @change="loadData">
          <option value="">All Settings</option>
          <option value="SNF">SNF</option>
          <option value="ALF">ALF</option>
        </select>
        <div class="flex items-center gap-1.5">
          <label class="text-[10px] font-medium text-gray-500 uppercase">From</label>
          <input type="date" v-model="startDate" @change="loadData" class="px-2 py-1 text-xs border border-gray-300 rounded" />
        </div>
        <div class="flex items-center gap-1.5">
          <label class="text-[10px] font-medium text-gray-500 uppercase">To</label>
          <input type="date" v-model="endDate" @change="loadData" class="px-2 py-1 text-xs border border-gray-300 rounded" />
        </div>
        <ExportMenu :disabled="!activeRows.length" :exports="[{ type: 'csv', action: exportCsv }]" />
      </div>
    </div>

    <!-- Summary cards -->
    <div v-if="totals" class="flex flex-wrap gap-2 mb-4">
      <div v-for="card in summaryCards" :key="card.label"
        class="flex flex-col items-center bg-white border border-gray-200 rounded px-3 py-2 flex-1 min-w-[90px]">
        <div class="text-[9px] font-semibold uppercase tracking-wider text-gray-400 mb-0.5 leading-tight text-center">{{ card.label }}</div>
        <div class="text-sm font-semibold text-gray-900 tabular-nums text-center">{{ card.value }}</div>
      </div>
    </div>

    <!-- Description -->
    <div class="mb-4 text-xs text-gray-500 leading-relaxed space-y-1.5">
      <p>Medicare Part A (facility) and Part B (professional) physical and occupational therapy claims for attributed patients, aggregated by facility. Click a row to see individual patient detail.</p>
      <div class="flex flex-wrap gap-x-6 gap-y-0.5">
        <span><strong class="text-gray-600">PT / OT Units</strong> — billed in 15-minute increments</span>
        <span><strong class="text-gray-600">Cost / Pt</strong> — total paid ÷ distinct patients</span>
        <span><strong class="text-gray-600">PBPM</strong> — total paid ÷ (patients × months in date range)</span>
        <span><strong class="text-gray-600">Units / Pt</strong> — total units ÷ patients</span>
        <span><strong class="text-gray-600">Hours</strong> — total units × 15 min ÷ 60</span>
      </div>
      <p>Cells highlighted <span class="text-amber-700 bg-amber-50 px-1 rounded">amber</span> or <span class="text-red-700 bg-red-50 px-1 rounded font-semibold">red</span> indicate elevated per-patient cost or utilization.</p>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="py-12 text-center text-sm text-gray-400">Loading therapy data…</div>

    <!-- Facility table -->
    <div v-else-if="facilities.length" class="border border-gray-200 rounded bg-white overflow-hidden" style="max-height: 500px; overflow-y: auto;">
      <table class="w-full text-sm">
        <thead class="sticky top-0 z-10">
          <tr class="bg-gray-50 border-b border-gray-200 text-left">
            <th class="px-3 py-2 font-semibold text-gray-700 cursor-pointer hover:text-gray-900 sensitive" @click="toggleSort(viewMode === 'provider' ? 'provider_name' : 'facility_name')">{{ viewMode === 'provider' ? 'Provider' : 'Facility' }} {{ (sortKey === 'facility_name' || sortKey === 'provider_name') ? (sortDir === 'asc' ? '▲' : '▼') : '' }}</th>
            <th class="px-3 py-2 font-semibold text-gray-700 text-right cursor-pointer hover:text-gray-900 sensitive" @click="toggleSort('patients')">Patients {{ sortKey === 'patients' ? (sortDir === 'asc' ? '▲' : '▼') : '' }}</th>
            <th class="px-3 py-2 font-semibold text-gray-700 text-right cursor-pointer hover:text-gray-900" @click="toggleSort('pt_units')">PT Units {{ sortKey === 'pt_units' ? (sortDir === 'asc' ? '▲' : '▼') : '' }}</th>
            <th class="px-3 py-2 font-semibold text-gray-700 text-right cursor-pointer hover:text-gray-900" @click="toggleSort('ot_units')">OT Units {{ sortKey === 'ot_units' ? (sortDir === 'asc' ? '▲' : '▼') : '' }}</th>
            <th class="px-3 py-2 font-semibold text-gray-700 text-right cursor-pointer hover:text-gray-900" @click="toggleSort('total_paid')">Total Paid {{ sortKey === 'total_paid' ? (sortDir === 'asc' ? '▲' : '▼') : '' }}</th>
            <th class="px-3 py-2 font-semibold text-gray-700 text-right cursor-pointer hover:text-gray-900" @click="toggleSort('cost_per_patient')">Cost / Pt {{ sortKey === 'cost_per_patient' ? (sortDir === 'asc' ? '▲' : '▼') : '' }}</th>
            <th class="px-3 py-2 font-semibold text-gray-700 text-right cursor-pointer hover:text-gray-900" @click="toggleSort('pbpm')">PBPM {{ sortKey === 'pbpm' ? (sortDir === 'asc' ? '▲' : '▼') : '' }}</th>
            <th class="px-3 py-2 font-semibold text-gray-700 text-right cursor-pointer hover:text-gray-900" @click="toggleSort('units_per_patient')">Units / Pt {{ sortKey === 'units_per_patient' ? (sortDir === 'asc' ? '▲' : '▼') : '' }}</th>
            <th class="px-3 py-2 font-semibold text-gray-700 text-right cursor-pointer hover:text-gray-900" @click="toggleSort('total_hours')">Hours {{ sortKey === 'total_hours' ? (sortDir === 'asc' ? '▲' : '▼') : '' }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(fac, i) in sortedFacilities" :key="fac.facility_name || fac.provider_npi || i"
            class="border-b border-gray-100 transition-colors"
            :class="[
              viewMode === 'facility' ? 'cursor-pointer' : '',
              selectedFacility === fac.facility_name ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : 'hover:bg-gray-50',
              i % 2 === 0 ? '' : 'bg-gray-25'
            ]"
            @click="viewMode === 'facility' ? selectFacility(fac) : null">
            <td class="px-3 py-2 font-medium text-gray-900 max-w-[260px] truncate sensitive" :title="viewMode === 'provider' ? fac.provider_name : fac.facility_name">{{ (viewMode === 'provider' ? fac.provider_name : fac.facility_name) || '(Unknown)' }}</td>
            <td class="px-3 py-2 text-right tabular-nums text-gray-700 sensitive">{{ n(fac.patients) }}</td>
            <td class="px-3 py-2 text-right tabular-nums text-gray-700">{{ n(fac.pt_units) }}</td>
            <td class="px-3 py-2 text-right tabular-nums text-gray-700">{{ n(fac.ot_units) }}</td>
            <td class="px-3 py-2 text-right tabular-nums text-gray-700">{{ dollar(fac.total_paid) }}</td>
            <td class="px-3 py-2 text-right tabular-nums" :class="costClass(fac.cost_per_patient)">{{ dollar(fac.cost_per_patient) }}</td>
            <td class="px-3 py-2 text-right tabular-nums" :class="costClass(fac.pbpm, 500, 1000)">{{ dollar(fac.pbpm) }}</td>
            <td class="px-3 py-2 text-right tabular-nums" :class="unitsClass(fac.units_per_patient)">{{ n1(fac.units_per_patient) }}</td>
            <td class="px-3 py-2 text-right tabular-nums text-gray-700">{{ n1(fac.total_hours) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty state -->
    <div v-else-if="!loading" class="py-12 text-center text-sm text-gray-400">No therapy claims found for this entity and date range.</div>

    <!-- Facility detail panel -->
    <div v-if="selectedFacility" class="mt-6">
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <h3 class="text-sm font-semibold text-gray-800">{{ selectedFacility }}</h3>
          <span class="text-xs text-gray-400 sensitive">{{ patientRows.length }} patients</span>
        </div>
        <button class="text-xs text-gray-400 hover:text-gray-600" @click="selectedFacility = null; patientRows = []; cptCodes = []">✕ Close</button>
      </div>

      <!-- CPT Code breakdown -->
      <div v-if="cptCodes.length" class="mb-4">
        <h4 class="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">CPT Code Breakdown</h4>
        <div class="border border-gray-200 rounded bg-white overflow-hidden" style="max-height: 260px; overflow-y: auto;">
          <table class="w-full text-sm">
            <thead class="sticky top-0 z-10">
              <tr class="bg-gray-50 border-b border-gray-200 text-left">
                <th class="px-3 py-1.5 font-semibold text-gray-700">CPT</th>
                <th class="px-3 py-1.5 font-semibold text-gray-700">Description</th>
                <th class="px-3 py-1.5 font-semibold text-gray-700">Type</th>
                <th class="px-3 py-1.5 font-semibold text-gray-700 text-right">Patients</th>
                <th class="px-3 py-1.5 font-semibold text-gray-700 text-right">Units</th>
                <th class="px-3 py-1.5 font-semibold text-gray-700 text-right">Units/Pt</th>
                <th class="px-3 py-1.5 font-semibold text-gray-700 text-right">Total Paid</th>
                <th class="px-3 py-1.5 font-semibold text-gray-700 text-right">Cost/Pt</th>
                <th class="px-3 py-1.5 font-semibold text-gray-700 text-right">Lines</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(c, i) in cptCodes" :key="c.hcpcs_code + c.therapy_type"
                class="border-b border-gray-100" :class="i % 2 === 0 ? '' : 'bg-gray-25'">
                <td class="px-3 py-1.5 font-mono text-xs text-gray-800">{{ c.hcpcs_code }}</td>
                <td class="px-3 py-1.5 text-gray-700 max-w-[260px] truncate" :title="c.hcpcs_description">{{ c.hcpcs_description || '—' }}</td>
                <td class="px-3 py-1.5"><span :class="c.therapy_type === 'PT' ? 'text-blue-700 bg-blue-50' : 'text-purple-700 bg-purple-50'" class="px-1.5 py-0.5 text-[10px] font-semibold rounded">{{ c.therapy_type }}</span></td>
                <td class="px-3 py-1.5 text-right tabular-nums text-gray-700 sensitive">{{ n(c.patients) }}</td>
                <td class="px-3 py-1.5 text-right tabular-nums text-gray-700">{{ n(c.total_units) }}</td>
                <td class="px-3 py-1.5 text-right tabular-nums text-gray-700">{{ n1(c.units_per_patient) }}</td>
                <td class="px-3 py-1.5 text-right tabular-nums text-gray-700">{{ dollar(c.total_paid) }}</td>
                <td class="px-3 py-1.5 text-right tabular-nums" :class="costClass(c.cost_per_patient, 5000, 10000)">{{ dollar(c.cost_per_patient) }}</td>
                <td class="px-3 py-1.5 text-right tabular-nums text-gray-700">{{ n(c.claim_lines) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Patient detail -->
      <div v-if="patientLoading" class="py-8 text-center text-sm text-gray-400">Loading patients…</div>
      <div v-else class="border border-gray-200 rounded bg-white overflow-hidden" style="max-height: 400px; overflow-y: auto;">
        <table class="w-full text-sm">
          <thead class="sticky top-0 z-10">
            <tr class="bg-gray-50 border-b border-gray-200 text-left">
              <th class="px-3 py-2 font-semibold text-gray-700">Patient</th>
              <th class="px-3 py-2 font-semibold text-gray-700 text-right">Total Paid</th>
              <th class="px-3 py-2 font-semibold text-gray-700 text-right">PT Paid</th>
              <th class="px-3 py-2 font-semibold text-gray-700 text-right">OT Paid</th>
              <th class="px-3 py-2 font-semibold text-gray-700 text-right">Units</th>
              <th class="px-3 py-2 font-semibold text-gray-700 text-right">Hours</th>
              <th class="px-3 py-2 font-semibold text-gray-700 text-right">Claims</th>
              <th class="px-3 py-2 font-semibold text-gray-700 text-right">Svc Days</th>
              <th class="px-3 py-2 font-semibold text-gray-700">First</th>
              <th class="px-3 py-2 font-semibold text-gray-700">Last</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(pat, i) in patientRows" :key="pat.bene_mbi_id"
              class="border-b border-gray-100"
              :class="i % 2 === 0 ? '' : 'bg-gray-25'">
              <td class="px-3 py-2 font-medium text-gray-900 sensitive">{{ [pat.bene_last_name, pat.bene_first_name].filter(Boolean).join(', ') }}</td>
              <td class="px-3 py-2 text-right tabular-nums text-gray-700">{{ dollar(pat.total_paid) }}</td>
              <td class="px-3 py-2 text-right tabular-nums text-gray-700">{{ dollar(pat.pt_paid) }}</td>
              <td class="px-3 py-2 text-right tabular-nums text-gray-700">{{ dollar(pat.ot_paid) }}</td>
              <td class="px-3 py-2 text-right tabular-nums text-gray-700">{{ n(pat.total_units) }}</td>
              <td class="px-3 py-2 text-right tabular-nums" :class="hoursClass(pat.total_hours)">{{ n1(pat.total_hours) }}</td>
              <td class="px-3 py-2 text-right tabular-nums text-gray-700">{{ pat.claim_count }}</td>
              <td class="px-3 py-2 text-right tabular-nums text-gray-700">{{ pat.service_days }}</td>
              <td class="px-3 py-2 text-gray-600">{{ fmtDate(pat.first_service) }}</td>
              <td class="px-3 py-2 text-gray-600">{{ fmtDate(pat.last_service) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import { blockSensitiveEgress } from '@/utils/demoEgress'

export default {
  name: 'CCLFReport_TherapyDeepDive',
  components: { ExportMenu },
  props: {
    contractId: { type: String, default: null },
    performanceYear: { type: Number, default: null },
    dosthru: { type: String, default: null },
    entityId: { type: Number, default: null },
  },

  data() {
    const now = new Date()
    const yearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())
    return {
      careSetting: '',
      facilities: [],
      providers: [],
      viewMode: 'facility',
      loading: false,
      startDate: yearAgo.toISOString().slice(0, 10),
      endDate: now.toISOString().slice(0, 10),
      selectedFacility: null,
      patientRows: [],
      cptCodes: [],
      patientLoading: false,
      sortKey: 'total_paid',
      sortDir: 'desc',
    }
  },

  computed: {
    activeRows() {
      return this.viewMode === 'provider' ? this.providers : this.facilities
    },
    sortedFacilities() {
      const rows = [...this.activeRows]
      const key = this.sortKey
      const dir = this.sortDir === 'asc' ? 1 : -1
      rows.sort((a, b) => {
        const va = a[key] ?? '', vb = b[key] ?? ''
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
        return String(va).localeCompare(String(vb)) * dir
      })
      return rows
    },
    totals() {
      const rows = this.activeRows
      if (!rows.length) return null
      const sum = (key) => rows.reduce((s, r) => s + (Number(r[key]) || 0), 0)
      return {
        patients: rows.reduce((s, r) => s + (r.patients || 0), 0),
        totalPaid: sum('total_paid'),
        ptPaid: sum('pt_paid'),
        otPaid: sum('ot_paid'),
        totalUnits: sum('total_units'),
        count: rows.length,
      }
    },
    summaryCards() {
      if (!this.totals) return []
      const t = this.totals
      const c = v => '$' + Math.round(v).toLocaleString()
      return [
        { label: this.viewMode === 'provider' ? 'Providers' : 'Facilities', value: t.count.toLocaleString() },
        { label: 'Patients', value: t.patients.toLocaleString() },
        { label: 'Total Paid', value: c(t.totalPaid) },
        { label: 'PT Cost', value: c(t.ptPaid) },
        { label: 'OT Cost', value: c(t.otPaid) },
        { label: 'Total Units', value: t.totalUnits.toLocaleString() },
        { label: 'Total Hours', value: Math.round(t.totalUnits * 15 / 60).toLocaleString() },
      ]
    },
  },

  watch: {

    contractId() { this.loadData() },
    performanceYear() { this.loadData() },
    dosthru() { this.loadData() },
    entityId() { this.loadData() },
  },

  mounted() { if (this.contractId && this.performanceYear) this.loadData() },

  methods: {
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDir = key === 'facility_name' ? 'asc' : 'desc'
      }
    },
    n(v) { return v != null ? Number(v).toLocaleString() : '--' },
    n1(v) { return v != null ? Number(v).toLocaleString('en-US', { maximumFractionDigits: 1 }) : '--' },
    dollar(v) { return v != null ? '$' + Math.round(Number(v)).toLocaleString() : '--' },

    costClass(v, warn = 10000, danger = 20000) {
      if (v == null) return 'text-gray-700'
      if (v >= danger) return 'text-red-700 font-semibold bg-red-50'
      if (v >= warn) return 'text-amber-700 bg-amber-50'
      return 'text-gray-700'
    },
    unitsClass(v) {
      if (v == null) return 'text-gray-700'
      if (v >= 1000) return 'text-red-700 font-semibold bg-red-50'
      if (v >= 500) return 'text-amber-700 bg-amber-50'
      return 'text-gray-700'
    },

    async loadData() {
      if (!this.contractId || !this.performanceYear) return
      this.loading = true
      this.selectedFacility = null
      this.patientRows = []
      this.cptCodes = []
      try {
        const { data } = await this.$axios.post('/api/portfolio_management/cclf-report/therapy-deep-dive/', {
          contract_id: this.contractId, performance_year: this.performanceYear,
          dosthru: this.dosthru, entity_id: this.entityId,
          care_setting: this.careSetting || undefined,
          start_date: this.startDate, end_date: this.endDate,
        })
        if (data.start_date) this.startDate = data.start_date
        if (data.end_date) this.endDate = data.end_date
        this.facilities = data.facilities || []
        this.providers = data.providers || []
      } catch (e) {
        console.error('Failed to load therapy data:', e)
      } finally {
        this.loading = false
      }
    },

    async selectFacility(fac) {
      const name = fac.facility_name
      if (this.selectedFacility === name) {
        this.selectedFacility = null
        this.patientRows = []
        this.cptCodes = []
        return
      }
      this.selectedFacility = name
      this.patientLoading = true
      try {
        const { data } = await this.$axios.post('/api/portfolio_management/cclf-report/therapy-patients/', {
          facility_name: name,
          entity_id: this.entityId,
          care_setting: this.careSetting || undefined,
          performance_year: this.performanceYear,
          start_date: this.startDate, end_date: this.endDate,
        })
        this.patientRows = data.patients || []
        this.cptCodes = data.cpt_codes || []
      } catch (e) {
        console.error('Failed to load patient detail:', e)
      } finally {
        this.patientLoading = false
      }
    },

    hoursClass(v) {
      if (v == null) return 'text-gray-700'
      if (v >= 500) return 'text-red-700 font-semibold bg-red-50'
      if (v >= 200) return 'text-amber-700 bg-amber-50'
      return 'text-gray-700'
    },
    fmtDate(v) { return v ? new Date(v).toLocaleDateString('en-US') : '--' },

    exportCsv() {
      if (!this.facilities.length) return
      if (blockSensitiveEgress('export')) return
      const headers = ['Facility','Patients','PT Units','OT Units','Total Paid','Cost/Pt','PBPM','Units/Pt','Hours']
      const rows = this.facilities.map(f => [
        f.facility_name, f.patients, f.pt_units, f.ot_units,
        f.total_paid, f.cost_per_patient, f.pbpm, f.units_per_patient, f.total_hours
      ])
      const csv = [headers, ...rows].map(r => r.map(c => typeof c === 'string' && c.includes(',') ? `"${c}"` : c).join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = 'therapy_by_facility.csv'
      a.click()
    },
  },
}
</script>
